import { Injectable } from "@angular/core";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import {
  getSafeAmount,
  getSafeSymbol,
  getDateString,
  normalizeDescription,
  isRealTransaction,
} from "./transaction.utils";

export const RD_STATUS = {
  CONFIRMED: "Confirmed",
  IRREGULAR: "Irregular",
} as const;

export interface RecurringRow {
  accountId: string;
  description: string;
  groupKey: string;
  frequency: string;
  count: number;
  avgAmount: number;
  symbol: string;
  hasVariableAmount: boolean;
  nextExpected: Date | null;
  monthlyEquivalent: number;
  status: string;
}

export interface DetailRow {
  date: string;
  amount: number;
  currency: string;
  change: number | null;
}

export type AccountOverrides = Record<string, "recurring" | "other">;

const FREQUENCY_RULES = [
  { min: 5, max: 9, label: "Weekly" },
  { min: 12, max: 16, label: "Fortnightly" },
  { min: 25, max: 35, label: "Monthly" },
  { min: 84, max: 98, label: "Quarterly" },
  { min: 330, max: 390, label: "Yearly" },
];

const MONTHLY_MULTIPLIERS: Record<string, number> = {
  Weekly: 4.33,
  Fortnightly: 2.17,
  Monthly: 1,
  Quarterly: 1 / 3,
  Yearly: 1 / 12,
  // User-overridden single-occurrence: frequency unknown, exclude from monthly total
  Single: 0,
};

@Injectable({ providedIn: "root" })
export class RecurringDetectorService {
  /** Detects each account separately (the same merchant paid from two accounts is two
   *  patterns, and merging them would break gap detection), then merges the results. */
  buildRowDataForAccounts(
    transactions: TransactionView[],
    accountIds: string[],
    overridesByAccount: Record<string, AccountOverrides>,
  ): { matchedRowData: RecurringRow[]; unmatchedRowData: RecurringRow[] } {
    const matchedRowData: RecurringRow[] = [];
    const unmatchedRowData: RecurringRow[] = [];
    for (const accountId of accountIds) {
      const rows = this.buildRowData(transactions, accountId, overridesByAccount[accountId] || {});
      matchedRowData.push(...rows.matchedRowData);
      unmatchedRowData.push(...rows.unmatchedRowData);
    }
    matchedRowData.sort((a, b) => b.monthlyEquivalent - a.monthlyEquivalent);
    return { matchedRowData, unmatchedRowData };
  }

  buildRowData(
    transactions: TransactionView[],
    accountId: string,
    overrides: AccountOverrides,
  ): { matchedRowData: RecurringRow[]; unmatchedRowData: RecurringRow[] } {
    const filtered = transactions.filter(
      (tx) => tx.accountLink?.id === accountId && isRealTransaction(tx) && tx.postDate,
    );

    const groups: Record<string, TransactionView[]> = {};
    for (const tx of filtered) {
      const key = normalizeDescription(tx.description);
      if (key === "unknown") {
        continue;
      }
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(tx);
    }

    const matchedRowData: RecurringRow[] = [];
    const unmatchedRowData: RecurringRow[] = [];

    for (const [key, g] of Object.entries(groups)) {
      const sorted = g
        .slice()
        .sort((a, b) => new Date(a.postDate).getTime() - new Date(b.postDate).getTime());
      const dates = sorted.map((t) => new Date(t.postDate));

      const displayName = sorted[sorted.length - 1]?.description?.trim() || key;

      let isRecurring = this.isGapConsistent(dates);
      if (overrides[key] === "recurring") {
        isRecurring = true;
      }
      if (overrides[key] === "other") {
        isRecurring = false;
      }

      const freqLabel = isRecurring
        ? this.classifyFrequency(dates)
        : g.length < 2
          ? "Single"
          : "Variable";

      let avgGap = 0;
      let nextExpected: Date | null = null;
      if (isRecurring && dates.length >= 2) {
        avgGap = this.calcAvgGapDays(dates);
        if (avgGap > 0) {
          nextExpected = new Date(dates[dates.length - 1].getTime() + avgGap * 24 * 60 * 60 * 1000);
        }
      }

      const amounts = sorted.map(getSafeAmount).filter((v) => v !== 0);
      const avgAmount =
        amounts.length > 0 ? amounts.reduce((a, b) => a + b, 0) / amounts.length : 0;
      const hasVariableAmount =
        amounts.length > 1 &&
        Math.abs(avgAmount) > 0 &&
        amounts.some((a) => Math.abs(a - avgAmount) / Math.abs(avgAmount) > 0.01);

      const multiplier =
        freqLabel === "Recurring" && avgGap > 0
          ? 30.44 / avgGap
          : (MONTHLY_MULTIPLIERS[freqLabel] ?? 0);
      const monthlyEquivalent =
        isRecurring && avgAmount !== 0 ? Math.abs(avgAmount) * multiplier : 0;

      const row: RecurringRow = {
        accountId,
        description: displayName,
        groupKey: key,
        frequency: freqLabel,
        count: g.length,
        avgAmount,
        symbol: getSafeSymbol(g[0]),
        hasVariableAmount,
        nextExpected,
        monthlyEquivalent,
        status: isRecurring ? RD_STATUS.CONFIRMED : RD_STATUS.IRREGULAR,
      };

      if (isRecurring) {
        matchedRowData.push(row);
      } else {
        unmatchedRowData.push(row);
      }
    }

    matchedRowData.sort((a, b) => b.monthlyEquivalent - a.monthlyEquivalent);
    return { matchedRowData, unmatchedRowData };
  }

  buildDetailRows(
    transactions: TransactionView[],
    groupKey: string,
    accountId: string,
  ): DetailRow[] {
    const sorted = transactions
      .filter(
        (tx) =>
          normalizeDescription(tx.description) === groupKey &&
          tx.accountLink?.id === accountId &&
          isRealTransaction(tx) &&
          tx.postDate,
      )
      .sort((a, b) => new Date(b.postDate).getTime() - new Date(a.postDate).getTime());

    return sorted.map((tx, i) => {
      const amount = getSafeAmount(tx);
      const prevAmount = i < sorted.length - 1 ? getSafeAmount(sorted[i + 1]) : null;
      return {
        date: getDateString(tx),
        amount,
        currency: getSafeSymbol(tx),
        change: prevAmount !== null ? Math.abs(amount) - Math.abs(prevAmount) : null,
      };
    });
  }

  private classifyFrequency(dates: Date[]): string {
    if (dates.length < 2) {
      return "Single";
    }
    const roundedGap = Math.round(this.calcAvgGapDays(dates));
    const rule = FREQUENCY_RULES.find((r) => roundedGap >= r.min && roundedGap <= r.max);
    return rule ? rule.label : "Recurring";
  }

  private calcAvgGapDays(dates: Date[]): number {
    if (dates.length < 2) {
      return 0;
    }
    let total = 0;
    for (let i = 1; i < dates.length; i++) {
      total += Math.abs(dates[i].getTime() - dates[i - 1].getTime()) / (1000 * 60 * 60 * 24);
    }
    return total / (dates.length - 1);
  }

  // Proportional tolerance: 15% of average gap, minimum 1 day. Requires at least 3 occurrences
  // (2 gaps) — a single gap is trivially "consistent" and would flag any two-transaction pair.
  private isGapConsistent(dates: Date[]): boolean {
    if (dates.length < 3) {
      return false;
    }
    const gaps: number[] = [];
    for (let i = 1; i < dates.length; i++) {
      gaps.push(Math.abs(dates[i].getTime() - dates[i - 1].getTime()) / (1000 * 60 * 60 * 24));
    }
    const avgGap = gaps.reduce((a, b) => a + b, 0) / gaps.length;
    if (avgGap < 1) {
      return false;
    }
    const tolerance = Math.max(1, avgGap * 0.15);
    return gaps.every((gap) => Math.abs(gap - avgGap) <= tolerance);
  }
}
